import { ThunkDispatch } from 'store/store';

import { AnyAction, MiddlewareAPI } from 'redux';
import Services from 'store/Services';
import {
    OrderFilterResultsChangedEvent,
    OrderFilterResultsChangedEventType,
} from 'app/store/orders/orderFilterResultsChangedEvent';
import {
    actionIsPortfolioSetActiveInputAction,
    FilteredPortfolioChangedEventType,
    FilteredPortfolioChangedPayload,
    PortfolioActionType,
    portfolioSetSelection,
} from './actions';
import {
    isOrdersStreamUpdatedAction,
    OrdersActionType,
    OrdersFilterResultsChanged,
} from 'app/store/orders/actions';
import { portfolioItemsAreEqual } from 'portfolio/PortfolioItem';
import State from 'store/State';
import { buildLogger } from '@instinet-analytics/core-lib';
import {
    getMappedStreamFields,
    mapOrderToPortfolioItem,
} from 'app/store/orders/mapping';
import { PortfolioSelectionWrapper } from 'portfolio/PortfolioSelectionWrapper';
import { selectPortfolioSelection } from 'portfolio/store/selectors';
import {
    buildPortfolioSelection,
    emptySelectionState,
    PortfolioSelection,
} from 'portfolio/PortfolioSelection';
import Order from 'communication/types/order/Order';
import { selectAppSettings } from 'app/store/appSettings/selectors';
import { isUpdateAppSettingsAction } from 'app/store/appSettings/actions';
import { dispatchSetPortfolioSelectionIfChanged as dispatchSetPortfolioSelectionIfChangedP } from './dispatcher';

export const portfolioMiddleware = (services: Services) => [
    publishFilteredPortfolioChangedEventOnOrderFilterResultsChangedMiddleware(
        services
    ),
    updatePortfolioOnStreamUpdateToSelectedOrders(services),
    updatePortfolioOnCombineSellsAndShortsChanged(services),
    clearPortfolioIfActiveInputChangesToNone(services),
    validateSymbolOnSingleSymbolSelection(services),
];

export function publishFilteredPortfolioChangedEventOnOrderFilterResultsChangedMiddleware(
    services: Services
) {
    return (middlewareAPI: MiddlewareAPI<ThunkDispatch<AnyAction>, State>) =>
        (next: ThunkDispatch<AnyAction>) =>
        (action: AnyAction) => {
            const { loggingService, portfolioEventService } = services;

            const logger = buildLogger(
                'Portfolio raiseFilteredPortfolioChangedAsyncOnOrderFilterResultsChangedMiddleware',
                loggingService
            );

            next(action);

            if (
                action.type !== OrdersActionType.ORDERS_FILTER_RESULTS_CHANGED
            ) {
                return;
            }

            const state = middlewareAPI.getState();

            const orderFilterResultsChanged =
                action as OrdersFilterResultsChanged;
            const payload = toFilteredPortfolioChangedPayload(
                orderFilterResultsChanged.payload!,
                state
            );

            if (payload === undefined) {
                logger.info(
                    'Ignoring order filter results changes as Portfolio is not affected'
                );
                return;
            }

            portfolioEventService.publishFilteredPortfolioChanged(payload);
        };
}

export function updatePortfolioOnStreamUpdateToSelectedOrders(
    services: Services
) {
    return (middlewareAPI: MiddlewareAPI<ThunkDispatch<AnyAction>, State>) =>
        (next: ThunkDispatch<AnyAction>) =>
        (action: AnyAction) => {
            const { loggingService } = services;

            const logger = buildLogger(
                'Portfolio updatePortfolioOnStreamUpdateToSelectedOrders',
                loggingService
            );

            next(action);

            if (!isOrdersStreamUpdatedAction(action)) {
                return;
            }

            // If no updates, nothing to do...
            const updatedEvent = action.payload!;
            if (updatedEvent.updated.length === 0) {
                return;
            }

            const state = middlewareAPI.getState();

            // If no orders selected, nothing to do...
            const selection = selectPortfolioSelection(state);
            if (
                !selection ||
                !selection.selectedOrders ||
                selection.selectedOrders.length === 0
            ) {
                return;
            }

            const nextSelectedOrders: Order[] = [];
            let selectedOrderWasUpdated = false;

            // Check if any of the selected orders were updated...
            for (const selectedOrder of selection.selectedOrders) {
                const updatedOrder =
                    updatedEvent.updated[selectedOrder.idx]?.next;
                if (!updatedOrder) {
                    nextSelectedOrders.push(selectedOrder);
                } else {
                    selectedOrderWasUpdated = true;
                    nextSelectedOrders.push(updatedOrder);
                }
            }

            if (selectedOrderWasUpdated) {
                const { combineSellsAndShorts } = selectAppSettings(state);
                const nextSelection = buildPortfolioSelection({
                    selectedOrders: nextSelectedOrders,
                    symbology: selection.symbology,
                    combineSellsAndShorts,
                });
                logger.debug('Selected order was updated', {
                    prev: selection.selectedOrders,
                    next: nextSelection.selectedOrders,
                });
                middlewareAPI.dispatch(portfolioSetSelection(nextSelection));
            }
        };
}

export function updatePortfolioOnCombineSellsAndShortsChanged(
    services: Services
) {
    return (middlewareAPI: MiddlewareAPI<ThunkDispatch<AnyAction>, State>) =>
        (next: ThunkDispatch<AnyAction>) =>
        (action: AnyAction) => {
            const { loggingService } = services;

            const logger = buildLogger(
                'Portfolio updatePortfolioOnCombineSellsAndShortsChanged',
                loggingService
            );

            next(action);

            if (!isUpdateAppSettingsAction(action)) {
                return;
            }

            if (action.payload?.combineSellsAndShorts === undefined) {
                return;
            }

            const state = middlewareAPI.getState();

            const selection = selectPortfolioSelection(state);
            if (
                !selection ||
                !selection.selectedOrders ||
                selection.selectedOrders.length === 0
            ) {
                // If no orders selected, nothing to do...
                return;
            }

            const { combineSellsAndShorts } = selectAppSettings(state);

            const nextSelection = buildPortfolioSelection({
                selectedOrders: selection.selectedOrders,
                symbology: selection.symbology,
                combineSellsAndShorts,
            });

            const prevSelectionWrapper = new PortfolioSelectionWrapper(
                selection
            );
            const nextSelectionWrapper = new PortfolioSelectionWrapper(
                nextSelection
            );

            if (
                !nextSelectionWrapper.positionSelectionDiffersByIdentityFrom(
                    prevSelectionWrapper
                )
            ) {
                // The change did not affect the position => nothing to do
                return;
            }

            logger.debug(
                'combineSellsAndShorts affects position: updating selection',
                {
                    prev: selection.selectedPositions,
                    next: nextSelection.selectedPositions,
                }
            );
            middlewareAPI.dispatch(portfolioSetSelection(nextSelection));
        };
}

function toFilteredPortfolioChangedPayload(
    payload: OrderFilterResultsChangedEvent,
    state: State
): FilteredPortfolioChangedPayload | undefined {
    if (payload.changedType === OrderFilterResultsChangedEventType.RESET) {
        return { changedType: FilteredPortfolioChangedEventType.RESET };
    }

    const { symbolField } = getMappedStreamFields(state);

    const added = payload
        .updates!.newMembers.filter((m) => m?.next)
        .map((m) => ({
            next: mapOrderToPortfolioItem(m!.next!, symbolField),
        }));

    const removed = payload
        .updates!.removedMembers.filter((m) => m?.prev)
        .map((m) => ({
            prev: mapOrderToPortfolioItem(m!.prev!, symbolField),
        }));

    const updated = payload
        .updates!.updatedMembers.filter((m) => m?.prev)
        .map((m) => {
            const prev = mapOrderToPortfolioItem(m!.prev!, symbolField);
            const next = mapOrderToPortfolioItem(m!.next!, symbolField);
            return { prev, next };
        })
        .filter((e) => !portfolioItemsAreEqual(e?.prev, e?.next));

    if (added.length === 0 && removed.length === 0 && updated.length === 0) {
        return undefined;
    }
    return {
        changedType: FilteredPortfolioChangedEventType.UPDATED,
        batchUpdateInProgress: payload.batchUpdateInProgress,
        updates: {
            added,
            removed,
            updated,
        },
    };
}

export function dispatchSetPortfolioSelectionIfChanged(
    middlewareAPI: MiddlewareAPI<ThunkDispatch<AnyAction>, State>,
    services: Services,
    nextSelectionState: PortfolioSelection
) {
    const { dispatch, getState } = middlewareAPI;

    dispatchSetPortfolioSelectionIfChangedP(
        getState(),
        dispatch,
        nextSelectionState
    );
}

export function clearPortfolioIfActiveInputChangesToNone(services: Services) {
    return (middlewareAPI: MiddlewareAPI<ThunkDispatch<AnyAction>, State>) =>
        (next: ThunkDispatch<AnyAction>) =>
        (action: AnyAction) => {
            const { loggingService, portfolioEventService } = services;

            next(action);

            if (!actionIsPortfolioSetActiveInputAction(action)) {
                return;
            }

            const logger = buildLogger(
                'Portfolio clearPortfolioIfActiveInputChangesToNone',
                loggingService
            );
            const { dispatch } = middlewareAPI;

            if (action.payload!.activeInputType === 'None') {
                logger.debug('Active input cleared => resetting portfolio');
                dispatch(portfolioSetSelection({ ...emptySelectionState }));
                portfolioEventService.publishFilteredPortfolioChanged({
                    changedType: FilteredPortfolioChangedEventType.RESET,
                });
            }
        };
}

/*
 * When a single symbol is selected, validate so we can save its symbolInfo in state
 */
export function validateSymbolOnSingleSymbolSelection(services: Services) {
    return (middlewareAPI: MiddlewareAPI<ThunkDispatch<AnyAction>, State>) =>
        (next: ThunkDispatch<AnyAction>) =>
        (action: AnyAction) => {
            const { symbolsService } = services;

            const previousState = middlewareAPI.getState();

            next(action);

            if (action.type !== PortfolioActionType.PORTFOLIO_SET_SELECTION) {
                return;
            }

            const state = middlewareAPI.getState();

            const selection = new PortfolioSelectionWrapper(
                selectPortfolioSelection(state)
            );

            if (!selection?.selectedSymbol) {
                return;
            }

            const previousSelection = new PortfolioSelectionWrapper(
                selectPortfolioSelection(previousState)
            );

            if (previousSelection.selectedSymbol !== selection.selectedSymbol) {
                symbolsService.validateSymbol(
                    selection.selectedSymbol,
                    selection.symbology
                );
            }
        };
}
